/**
 * Die fünf Abschnitte, alle offen (Board „Preisautomatik · Einstellungen“). Ein Feld zeigt den Fehler
 * des Formulars, sonst den Befund des Servers zu genau diesem Entwurf.
 */
import { useEffect } from 'react';
import type { UseFieldArrayReturn, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type {
  PricingConfigurationInput,
  PricingConfigurationValidationResponse,
  PricingStrategyProfileResponse,
} from '@/shared/api';
import { ConfigurationSection } from './ConfigurationSection';
import type { SectionKey, SectionMarker } from './configurationSections';
import { MinimumStaySection } from './MinimumStaySection';
import { PriceRulesSection } from './PriceRulesSection';
import type { PricingConfigurationFormValues } from './pricingConfigurationSchema';
import { SafetyPreviewSection } from './SafetyPreviewSection';
import { ScheduleSection } from './ScheduleSection';
import { StatusStrategySection } from './StatusStrategySection';

interface Props {
  propertyId?: string;
  form: UseFormReturn<PricingConfigurationFormValues>;
  windows: UseFieldArrayReturn<PricingConfigurationFormValues, 'publicationWindows'>;
  tiers: UseFieldArrayReturn<PricingConfigurationFormValues, 'minimumStayLeadTimeTiers'>;
  configuration: PricingConfigurationInput;
  profiles: PricingStrategyProfileResponse[];
  activeProfile?: PricingStrategyProfileResponse;
  activeProfileProblem?: string | null;
  onRetryActiveProfile?: () => void;
  validation?: PricingConfigurationValidationResponse;
  warnings?: string[];
  publicationControlsAvailable: boolean;
  markers: Record<SectionKey, SectionMarker>;
  serverByField: Record<string, string>;
  serverErrors: number;
  serverChecked: boolean;
}

type ErrorNode = { message?: string; root?: { message?: string } } | undefined;

export function PricingConfigurationFields(props: Props) {
  const { t } = useTranslation();
  const { form, markers, configuration } = props;
  const mode = form.watch('mode');
  useEffect(() => {
    if (mode === 'Off' && form.getValues('minimumStayTiersEnabled')) {
      form.setValue('minimumStayTiersEnabled', false, { shouldDirty: true });
    }
  }, [form, mode]);
  const errorFor = (path: string) => {
    const node = path
      .split('.')
      .reduce<unknown>(
        (current, key) => (current as Record<string, unknown> | undefined)?.[key],
        form.formState.errors,
      ) as ErrorNode;
    const message = node?.message ?? node?.root?.message;
    if (message) {
      return t(`admin.dynamicPricing.configuration.validation.${message}`, {
        defaultValue: message,
      });
    }
    return path.includes('.') ? undefined : props.serverByField[path];
  };
  const warnings = [...new Set([...(props.warnings ?? []), ...(props.validation?.warnings ?? [])])];

  return (
    <>
      <ConfigurationSection section="status" marker={markers.status}>
        <StatusStrategySection
          form={form}
          profiles={props.profiles}
          activeProfileVersionId={configuration.strategyProfileVersionId}
          activeProfile={props.activeProfile}
          activeProfileProblem={props.activeProfileProblem}
          onRetryActiveProfile={props.onRetryActiveProfile}
          publicationControlsAvailable={props.publicationControlsAvailable}
          errorFor={errorFor}
        />
      </ConfigurationSection>
      <ConfigurationSection section="prices" marker={markers.prices}>
        <PriceRulesSection
          propertyId={props.propertyId}
          form={form}
          currency={configuration.currency}
          errorFor={errorFor}
        />
      </ConfigurationSection>
      <ConfigurationSection section="minStay" marker={markers.minStay}>
        <MinimumStaySection form={form} tiers={props.tiers} errorFor={errorFor} />
      </ConfigurationSection>
      <ConfigurationSection section="schedule" marker={markers.schedule}>
        <ScheduleSection
          form={form}
          windows={props.windows}
          horizonDays={configuration.horizonDays}
          errorFor={errorFor}
        />
      </ConfigurationSection>
      <ConfigurationSection section="safety" marker={markers.safety}>
        <SafetyPreviewSection
          serverErrors={props.serverErrors}
          checked={props.serverChecked}
          warnings={warnings}
          horizonDays={configuration.horizonDays}
        />
      </ConfigurationSection>
    </>
  );
}
